import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { ActionPanel } from './ActionPanel';
import { MobileTabBar } from './MobileTabBar';
import { ModalsRoot } from '../modals/ModalsRoot';
import { Toaster } from '../ui/Toaster';
import { ConfirmRoot } from '../ui/ConfirmDialog';
import { MomentRoot } from '../ui/MomentOverlay';
import { useUIStore } from '../../stores/ui.store';
import { useAuthStore } from '../../stores/auth.store';
import { Navigate } from 'react-router-dom';

export function AppShell() {
  const { density, canvas, sidebarCollapsed, setSidebarCollapsed } = useUIStore();
  const isAuth = useAuthStore((s) => s.user);

  useEffect(() => {
    document.body.dataset.density = density;
    document.body.dataset.canvas = canvas;
    // La barra di stato del telefono prende il colore del fondo dell'app, così
    // in cima allo schermo non c'è una striscia di un altro colore. Il valore
    // nell'index.html è solo il punto di partenza prima che React parta; da
    // qui in poi comanda il tema scelto. (Il tema scuro oggi non è ancora
    // selezionabile: quando lo sarà, questa riga funziona già.)
    // `:not([data-katana])`: mentre lo sfodero del logo e' a schermo c'e' un
    // theme-color in piu', messo da index.html, che tiene la barra di stato
    // dello stesso inchiostro del foglio. Se lo riscrivessimo qui tornerebbe
    // chiara e su iPhone comparirebbe una banda slavata in cima. Se ne va da
    // sola insieme al foglio (lib/katana.ts).
    document
      .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]:not([data-katana])')
      .forEach((m) => { m.content = canvas === 'dark' ? '#1a1a1e' : '#f6f6f7'; });
  }, [density, canvas]);

  // Auto-collapse della sidebar sotto i 1100px (laptop piccoli, finestre
  // affiancate): sopra si riapre. matchMedia → zero polling.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1100px)');
    const apply = () => setSidebarCollapsed(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [setSidebarCollapsed]);

  // Barra in alto di vetro: sul computer la pagina le scorre sotto e il vetro
  // si accende solo quando c'è davvero qualcosa sotto (index.css, "Barra in
  // alto di vetro"). Qui si dice al CSS se la pagina è scesa dalla cima.
  // L'attributo si scrive a mano sul guscio e solo quando cambia: nessun
  // render di React mentre si scorre.
  const shellRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const shell = shellRef.current;
    const main = mainRef.current;
    if (!shell || !main) return;
    let scrolled = false;
    const update = () => {
      const now = main.scrollTop > 0;
      if (now === scrolled) return;
      scrolled = now;
      if (now) shell.setAttribute('data-main-scrolled', '');
      else shell.removeAttribute('data-main-scrolled');
    };
    update();
    main.addEventListener('scroll', update, { passive: true });
    return () => {
      main.removeEventListener('scroll', update);
      shell.removeAttribute('data-main-scrolled');
    };
  }, [isAuth]);

  if (!isAuth) return <Navigate to="/login" replace />;

  return (
    <div ref={shellRef} className="os-shell" data-sidebar={sidebarCollapsed ? 'collapsed' : 'expanded'}>
      <Sidebar />
      <Topbar />
      <main ref={mainRef} className="os-main">
        <Outlet />
      </main>
      {/* Barra fissa in basso: esiste solo sotto i 768px (lo decide il CSS,
          non un matchMedia), sopra non si vede e il desktop resta identico. */}
      <MobileTabBar />
      {/* Slide-over "Da fare": fixed, fuori dal flusso del main (toggle in Topbar / Ctrl+.) */}
      <ActionPanel />
      <ModalsRoot />
      <Toaster />
      <ConfirmRoot />
      <MomentRoot />
    </div>
  );
}
